import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { asList } from '../../utils/apiList';
import logo from './CREATIVE-E-LEARNING.png';


import { API_URL } from '../../config';
interface NavbarProps {
  user?: {
    name: string;
    role: string;
    avatar?: string;
  };
  onLogout?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ user: propUser, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setUserData(response.data);
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API_URL}/api/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        let count = 0;
        if (typeof res.data === 'number') count = res.data;
        else if (res.data?.count != null) count = Number(res.data.count);

        // Fallback: if API returns suspicious zero, compute from list
        if (count === 0) {
          try {
            const listRes = await axios.get(`${API_URL}/api/notifications/`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const list = asList(listRes.data);
            count = list.filter((n: any) => !n.is_read).length;
          } catch {}
        }
        setUnreadCount(count);
        try { window.dispatchEvent(new CustomEvent('notifications:unread', { detail: count })); } catch {}
      } catch (e) {
        // ignore
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onUnread = (e: Event) => {
      const anyEvent = e as any;
      const next = typeof anyEvent.detail === 'number' ? anyEvent.detail : 0;
      setUnreadCount(next);
    };
    window.addEventListener('notifications:unread', onUnread);
    return () => window.removeEventListener('notifications:unread', onUnread);
  }, []);

  // Handle click outside profile popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  // Use userData if available, otherwise fall back to propUser
  const displayUser = userData || propUser;

  // Function to get the correct role-based path
  const getRoleBasedPath = (path: string) => {
    if (!displayUser || !displayUser.role) return path;
    
    const role = displayUser.role.toLowerCase();
    if (['teacher', 'admin', 'student'].includes(role)) {
      return `/${role}${path}`;
    }
    return path;
  };

  return (
    <nav className="bg-white border-b border-[#E2E8F0] z-50 relative">
      <div className="flex h-16">
        {/* Left dark block for Logo (matches sidebar width) */}
        <div className="hidden md:flex w-64 bg-[#111827] flex-shrink-0 items-center justify-center">
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="Creative E-Learning" 
              className="h-12 w-auto"
            />
          </Link>
        </div>

        {/* Right side navbar content */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 flex justify-between bg-white">
          <div className="flex items-center">
            {/* Mobile Logo */}
            <div className="md:hidden flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src={logo} 
                  alt="Creative E-Learning" 
                  className="h-10 w-auto"
                />
              </Link>
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-center font-kumbh">
            {displayUser ? (
              <div className="flex items-center">
                {displayUser.role && displayUser.role.toLowerCase && displayUser.role.toLowerCase() === 'student' ? (
                  <Link to="/student/notifications" title="Notifications" className="mr-3 text-gray-600 hover:text-gray-600 flex items-center relative">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a7 7 0 0 0-7 7v3.586L3.293 14.293A1 1 0 0 0 4 16h16a1 1 0 0 0 .707-1.707L19 12.586V9a7 7 0 0 0-7-7z" />
                      <path d="M8 17a4 4 0 0 0 8 0H8z" />
                    </svg>
                    <span className={`absolute -top-1 -right-1 min-w-[1rem] h-4 px-1 rounded-full text-white text-[10px] leading-4 text-center ${unreadCount > 0 ? 'bg-red-600' : 'bg-gray-300'}`}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  </Link>
                ) : null}
                <div className="ml-3 relative" ref={profileRef}>
                  <div>
                    <button
                      type="button"
                      className="bg-white rounded-lg-full flex items-center px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white gap-2 shadow-md transition active:scale-95"
                      id="user-menu-button"
                      aria-expanded="false"
                      aria-haspopup="true"
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                    >
                      <span className="sr-only">Open user menu</span>
                      <div className="h-6 w-6 rounded-full bg-[#1a1a1a] flex items-center justify-center text-white">
                        {displayUser.avatar ? (
                          <img
                            className="h-6 w-6 rounded-full"
                            src={displayUser.avatar}
                            alt={displayUser.name}
                          />
                        ) : (
                          <span className="text-xs">
                            {displayUser.role === 'admin' ? 'A' : displayUser.role === 'teacher' ? 'T' : displayUser.role === 'student' ? 'S' : 'U'}
                          </span>
                        )}
                      </div>
                      <span className="text-gray-600">{displayUser?.full_name || displayUser?.name}</span>
                    </button>
                  </div>
                  {isProfileOpen && (
                    <div
                      className="origin-top-right z-50 absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu-button"
                      tabIndex={-1}
                    >
                      <div className="block px-4 py-2 text-sm text-gray-600 border-b">
                        <div className="font-medium">{displayUser?.full_name || displayUser?.name}</div>
                        <div className="text-gray-600 capitalize">{displayUser?.role}</div>
                      </div>
                      <Link
                        to={getRoleBasedPath('/profile')}
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-0"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        Your Profile
                      </Link>
                      <Link
                        to={getRoleBasedPath('/settings')}
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-1"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        Settings
                      </Link>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 shadow-md transition active:scale-95"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-2"
                        onClick={() => {
                          setIsProfileOpen(false);
                          onLogout && onLogout();
                        }}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex space-x-4">
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="bg-[#4F46E5] text-white hover:bg-[#4338CA] px-3 py-2 rounded-md text-sm font-medium"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-lg text-gray-600 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#4F46E5] shadow-md transition active:scale-95"
              aria-controls="mobile-menu"
              aria-expanded="false"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div
        className={`${isMenuOpen ? 'block' : 'hidden'} sm:hidden`}
        id="mobile-menu"
      >
        {/* <div className="pt-2 pb-3 space-y-1">
          <Link
            to="/dashboard"
            className="bg-blue-50 border-blue-500 text-blue-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            Dashboard
          </Link>
          <Link
            to="/courses"
            className="border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-600 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            Courses
          </Link>
          <Link
            to="/exams"
            className="border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-600 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            Exams
          </Link>
          {user && (user.role === 'admin' || user.role === 'superadmin' || user.role === 'teacher') && (
            <Link
              to="/questions"
              className="border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-600 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Questions
            </Link>
          )}
          {user && (user.role === 'admin' || user.role === 'superadmin') && (
            <Link
              to="/users"
              className="border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-600 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Users
            </Link>
          )}
        </div> */}
        {displayUser ? (
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                {displayUser.avatar ? (
                  <img
                    className="h-10 w-10 rounded-full"
                    src={displayUser.avatar}
                    alt={displayUser.name}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-[#1a1a1a] flex items-center justify-center text-white">
                    {displayUser.role === 'admin' ? 'A' : displayUser.role === 'teacher' ? 'T' : displayUser.role === 'student' ? 'S' : 'U'}
                  </div>
                )}
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-900">
                  {displayUser?.full_name || displayUser?.name}
                </div>
                <div className="text-sm font-medium text-gray-600 capitalize">
                  {displayUser?.role}
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Link
                to={getRoleBasedPath('/profile')}
                className="block px-4 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                onClick={() => setIsMenuOpen(false)}
              >
                Your Profile
              </Link>
              <Link
                to={getRoleBasedPath('/settings')}
                className="block px-4 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                onClick={() => setIsMenuOpen(false)}
              >
                Settings
              </Link>
              <button
                className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 shadow-md transition active:scale-95"
                onClick={() => {
                  setIsMenuOpen(false);
                  onLogout && onLogout();
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        ) : (
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center justify-around">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 px-4 py-2 text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="bg-[#4F46E5] text-white hover:bg-[#4338CA] px-4 py-2 rounded-md text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign up
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
